import { useMemo, useState, useCallback } from 'react';
import { api } from '../api/client';
import { invalidate } from '../api/cache.js';
import { Badge, PRIORITY_TONE, STATUS_TONE, fmtDate, daysUntil, label } from './ui.jsx';
import { t } from '../i18n/index.jsx';

const COLUMNS = ['todo', 'in_progress', 'blocked', 'review', 'done'];

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function DragHandle() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="4" r="1.2" />
      <circle cx="11" cy="4" r="1.2" />
      <circle cx="5" cy="8" r="1.2" />
      <circle cx="11" cy="8" r="1.2" />
      <circle cx="5" cy="12" r="1.2" />
      <circle cx="11" cy="12" r="1.2" />
    </svg>
  );
}

function TaskCard({ task, canEdit, dragging, onCardClick, onDragStart, onDragEnd }) {
  const d = daysUntil(task.due_date);
  const overdue = d != null && d < 0 && task.status !== 'done';
  const soon = d != null && d >= 0 && d <= 2 && task.status !== 'done';
  const itemCount = Number(task.item_count) || 0;
  const itemDone = Number(task.item_done) || 0;

  return (
    <div
      className={`kcard ${dragging ? 'dragging' : ''}`}
      draggable={canEdit}
      onDragStart={(e) => onDragStart(e, task)}
      onDragEnd={onDragEnd}
      onClick={() => onCardClick?.(task)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCardClick?.(task);
        }
      }}
    >
      <div className="kcard-top">
        <div className="kcard-title" title={task.title}>{task.title}</div>
        {canEdit && (
          <button
            type="button"
            className="kdrag"
            aria-label={t('Drag to move')}
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
          >
            <DragHandle />
          </button>
        )}
      </div>

      <div className="kcard-meta">
        <Badge tone={PRIORITY_TONE[task.priority] || 'gray'}>{label(task.priority)}</Badge>
        {task.due_date && (
          <span className={`kchip due ${overdue ? 'overdue' : soon ? 'soon' : ''}`}>
            {overdue ? t('{n}d overdue', { n: -d }) : d === 0 ? t('today') : d != null && d <= 7 ? t('in {n}d', { n: d }) : fmtDate(task.due_date)}
          </span>
        )}
        {itemCount > 0 && (
          <span className="kchip muted-chip" title={t('Checklist')}>✓ {itemDone}/{itemCount}</span>
        )}
      </div>

      <div className="kcard-foot">
        {(task.project_name || task.fund_client_name) ? (
          <span
            className="kchip project-chip"
            title={task.project_name || [task.fund_client_name, task.fund_name].filter(Boolean).join(' · ')}
          >
            {task.project_name || task.fund_client_name}
          </span>
        ) : (
          <span className="kchip muted-chip">
            {task.fund_client_id || task.fund_client_guess ? t('Fund client') : t('No project')}
          </span>
        )}
        {task.assignee_name ? (
          <span className="k-assignee" title={task.assignee_name}>
            <span className="k-avatar">{initials(task.assignee_name)}</span>
            <span className="k-assignee-name">{task.assignee_name.split(/\s+/)[0]}</span>
          </span>
        ) : (
          <span className="kchip muted-chip">{t('Unassigned')}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Lightweight native Kanban — all cards render (no virtualization bugs),
 * HTML5 drag-and-drop, uses existing .kanban / .kcard styles.
 */
export default function TaskKanban({ tasks, canEdit, onCardClick, onStatusChange }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(COLUMNS.map((s) => [s, []]));
    for (const task of tasks || []) {
      const key = COLUMNS.includes(task.status) ? task.status : 'todo';
      map[key].push(task);
    }
    return map;
  }, [tasks]);

  const onDragStart = useCallback((e, task) => {
    if (!canEdit) return;
    setDragId(task.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(task.id));
    requestAnimationFrame(() => {
      e.target?.closest?.('.kcard')?.classList.add('dragging');
    });
  }, [canEdit]);

  const onDragEnd = useCallback(() => {
    setDragId(null);
    setOverCol(null);
  }, []);

  const moveTask = useCallback(async (taskId, newStatus) => {
    const id = Number(taskId);
    const prev = (tasks || []).find((x) => Number(x.id) === id);
    if (!prev || prev.status === newStatus) return;

    onStatusChange?.(id, newStatus);
    setBusyId(id);
    try {
      await api.patch(`/tasks/${id}/status`, { status: newStatus });
      invalidate('/tasks');
    } catch {
      onStatusChange?.(id, prev.status);
    } finally {
      setBusyId(null);
    }
  }, [tasks, onStatusChange]);

  function onDropColumn(e, status) {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || dragId;
    setOverCol(null);
    setDragId(null);
    if (id != null) moveTask(id, status);
  }

  return (
    <div className="kanban" role="list">
      {COLUMNS.map((status) => {
        const colTasks = byStatus[status];
        return (
          <div
            key={status}
            className={`kcol ${overCol === status ? 'droppable' : ''}`}
            onDragOver={(e) => {
              if (!canEdit) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (overCol !== status) setOverCol(status);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setOverCol(null);
            }}
            onDrop={(e) => onDropColumn(e, status)}
          >
            <div className="kcol-head">
              <Badge tone={STATUS_TONE[status] || 'gray'}>{label(status)}</Badge>
              <span className="count">{colTasks.length}</span>
            </div>
            <div className="kcol-body">
              {colTasks.length === 0 ? (
                <div className="kempty">{t('No cards')}</div>
              ) : (
                colTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    canEdit={canEdit && busyId !== task.id}
                    dragging={dragId === task.id}
                    onCardClick={onCardClick}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
